import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Download, Printer, RefreshCcw } from "lucide-react";
import { PageHeader, Pill, SectionCard } from "../components/Ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { api, extractError } from "../lib/api";
import { downloadApiFile, openApiPrintView } from "../lib/downloadApiFile";
import { formatListDate, formatListDateTime, todayIsoDate } from "../lib/purchaseListMeta";
import { toast } from "sonner";
import {
  MajorKpiGrid,
  formatCurrency,
  formatPct,
  formatQty,
  verdictTone,
} from "./purchase/purchaseReportShared";

const EMPTY_FILTERS = {
  from_date: todayIsoDate() || "",
  to_date: todayIsoDate() || "",
  variety: "",
  party: "",
  party_kind: "",
};

export default function DailyMillReportPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [tab, setTab] = useState("overview");
  const [openDay, setOpenDay] = useState("");

  const params = useMemo(
    () => ({
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
      variety: filters.variety || undefined,
      party: filters.party || undefined,
      party_kind: filters.party_kind || undefined,
    }),
    [filters],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/daily-ops/report", { params });
      setPayload(data);
      if (data?.from_date && data.from_date === data.to_date) setOpenDay(data.from_date);
      else setOpenDay("");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async (mode) => {
    const stamp = `${filters.from_date || "from"}-to-${filters.to_date || "to"}`;
    try {
      if (mode === "excel") {
        await downloadApiFile("/daily-ops/export/excel", `daily-mill-report-${stamp}.xlsx`, params);
        toast.success("Excel download started");
        return;
      }
      if (mode === "pdf") {
        await downloadApiFile("/daily-ops/export/pdf", `daily-mill-report-${stamp}.pdf`, params);
        toast.success("PDF download started");
        return;
      }
      await openApiPrintView("/daily-ops/export/html", params);
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const kpis = payload?.consolidated || {};
  const days = payload?.days || [];
  const sameDay = payload?.from_date && payload.from_date === payload.to_date;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="daily-mill-report-page">
      <PageHeader
        eyebrow="Overview"
        title="Daily mill report"
        description="What happened at the mill: vehicles unloaded, weight, payables, lab, and a full day book. Pick one day or a period."
        actions={
          <>
            <Button type="button" variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
            <Button type="button" variant="outline" onClick={() => handleExport("print")}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("excel")}>Excel (all sheets)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>PDF presentation</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <SectionCard title="Period and filters">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            title="From date"
            aria-label="From date"
            value={filters.from_date}
            onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
            className="h-8 w-[9.5rem] shrink-0 px-2 text-sm"
          />
          <Input
            type="date"
            title="To date"
            aria-label="To date"
            value={filters.to_date}
            onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
            className="h-8 w-[9.5rem] shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Variety"
            aria-label="Variety"
            value={filters.variety}
            onChange={(e) => setFilters((f) => ({ ...f, variety: e.target.value }))}
            className="h-8 w-36 shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Party"
            aria-label="Party"
            value={filters.party}
            onChange={(e) => setFilters((f) => ({ ...f, party: e.target.value }))}
            className="h-8 w-36 shrink-0 px-2 text-sm"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { value: "", label: "All parties" },
              { value: "FARMER", label: "Farmer" },
              { value: "SUPPLIER", label: "Supplier" },
            ].map((option) => (
              <Button
                key={option.label}
                type="button"
                size="sm"
                variant={filters.party_kind === option.value ? "default" : "outline"}
                className={`h-8 px-2.5 text-xs ${filters.party_kind === option.value ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}`}
                onClick={() => setFilters((f) => ({ ...f, party_kind: option.value }))}
              >
                {option.label}
              </Button>
            ))}
            <Button type="button" size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => setFilters(EMPTY_FILTERS)}>
              Today
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {payload ? `${payload.from_date} → ${payload.to_date}` : "Select dates"} · IST calendar days
        </p>
      </SectionCard>

      <MajorKpiGrid
        items={[
          { label: "Vehicles unloaded", value: kpis.vehicles_unloaded ?? 0, accent: true },
          { label: "Net weight", value: `${formatQty(kpis.net_qtl)} qtl` },
          { label: "Avg weight / vehicle", value: kpis.avg_net_qtl != null ? `${formatQty(kpis.avg_net_qtl)} qtl` : "—" },
          { label: "Total payable", value: formatCurrency(kpis.total_payable), accent: true },
          { label: "Lab reports", value: kpis.lab_reports ?? 0, sub: `${kpis.lab_pass || 0} pass · ${kpis.lab_fail || 0} fail` },
          { label: "Avg moisture", value: kpis.lab_avg_moisture != null ? formatPct(kpis.lab_avg_moisture) : "—" },
          { label: "Gate in / out", value: `${kpis.vehicles_in ?? 0} / ${kpis.vehicles_out ?? 0}` },
          { label: "Gross / tare weigh", value: `${kpis.weigh_gross ?? 0} / ${kpis.weigh_tare ?? 0}` },
        ]}
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="overview">{sameDay ? "Day book" : "Each day"}</TabsTrigger>
          <TabsTrigger value="unloading">Unloading</TabsTrigger>
          <TabsTrigger value="lab">Lab</TabsTrigger>
          <TabsTrigger value="payables">Payables</TabsTrigger>
          <TabsTrigger value="activity">Everything</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <SectionCard
            title={sameDay ? "This day" : "Consolidated period with a row for each day"}
            subtitle="Click a date to highlight that day's rows in the other tabs."
          >
            <SimpleTable
              columns={["Date", "Unloaded", "Net qtl", "Avg qtl", "Payable", "Lab", "Gate in", "Gate out", "Purchases"]}
              rows={days.map((row) => {
                const k = row.kpis || {};
                const active = openDay === row.date;
                return [
                  <button
                    key={row.date}
                    type="button"
                    className={`link-brand font-medium ${active ? "underline" : ""}`}
                    onClick={() => setOpenDay(row.date === openDay ? "" : row.date)}
                  >
                    {formatListDate(`${row.date}T12:00:00+05:30`) || row.date}
                  </button>,
                  k.vehicles_unloaded ?? 0,
                  formatQty(k.net_qtl),
                  k.avg_net_qtl != null ? formatQty(k.avg_net_qtl) : "—",
                  formatCurrency(k.total_payable),
                  k.lab_reports ?? 0,
                  k.vehicles_in ?? 0,
                  k.vehicles_out ?? 0,
                  k.purchases_started ?? 0,
                ];
              })}
              emptyMessage={loading ? "Loading daily figures…" : "No days in this period."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="unloading">
          <SectionCard title="Vehicles unloaded">
            <SimpleTable
              columns={["When", "Purchase", "Vehicle", "Party", "Variety", "Bags", "Weight qtl"]}
              rows={filterByDay(payload?.unloading, openDay).map((row) => [
                formatListDateTime(row.at) || row.date,
                row.purchase_code || "—",
                row.vehicle_number || "—",
                row.party_name || "—",
                row.variety || "—",
                row.bags ?? "—",
                formatQty(row.weight_qtl),
              ])}
              emptyMessage={loading ? "Loading unloading…" : "No unloading in this period."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="lab">
          <SectionCard title="Lab reports">
            <SimpleTable
              columns={["When", "Purchase", "Vehicle", "Party", "Phase", "Verdict", "Moisture", "Broken", "Admixture"]}
              rows={filterByDay(payload?.lab, openDay).map((row) => [
                formatListDateTime(row.at) || row.date,
                row.purchase_code || "—",
                row.vehicle_number || "—",
                row.party_name || "—",
                row.phase || "—",
                <Pill key={`${row.purchase_code}-v`} tone={verdictTone(row.verdict)}>{row.verdict || "—"}</Pill>,
                row.moisture_pct != null ? formatPct(row.moisture_pct) : "—",
                row.broken_pct != null ? formatPct(row.broken_pct) : "—",
                row.admixture_pct != null ? formatPct(row.admixture_pct) : "—",
              ])}
              emptyMessage={loading ? "Loading lab…" : "No lab reports in this period."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="payables">
          <SectionCard title="Material vouchers / payable">
            <SimpleTable
              columns={["When", "Voucher", "Purchase", "Party", "Net qtl", "Rate", "Gross", "Payable"]}
              rows={filterByDay(payload?.payables, openDay).map((row) => [
                formatListDateTime(row.at) || row.date,
                row.voucher_code || "—",
                row.purchase_code || "—",
                row.party_name || "—",
                formatQty(row.net_qtl),
                formatCurrency(row.rate_per_qtl),
                formatCurrency(row.gross_amount),
                formatCurrency(row.net_payable),
              ])}
              emptyMessage={loading ? "Loading payables…" : "No material vouchers in this period."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="activity">
          <SectionCard title="Everything that happened" subtitle="Gate, purchase, weigh, unload, lab, and vouchers in time order.">
            <div className="space-y-2">
              {filterByDay(payload?.activity, openDay).length ? (
                filterByDay(payload?.activity, openDay).map((row, idx) => (
                  <div key={`${row.at}-${idx}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border/60 py-2">
                    <span className="text-xs font-mono text-muted-foreground w-36 shrink-0">{formatListDateTime(row.at) || row.date}</span>
                    <Pill tone="default">{String(row.kind || "").replaceAll("_", " ")}</Pill>
                    <span className="text-sm font-medium">{row.title}</span>
                    {row.detail ? <span className="text-sm text-muted-foreground">{row.detail}</span> : null}
                  </div>
                ))
              ) : (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  {loading ? "Loading activity…" : "Nothing recorded in this period."}
                </p>
              )}
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function filterByDay(rows, day) {
  const list = Array.isArray(rows) ? rows : [];
  if (!day) return list;
  return list.filter((row) => row.date === day);
}

function SimpleTable({ columns, rows, emptyMessage }) {
  if (!rows.length) {
    return <div className="py-12 text-center text-sm text-muted-foreground">{emptyMessage}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-sm min-w-[720px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground bg-muted border-b border-border">
            {columns.map((column) => (
              <th key={column} className="px-3 py-2">{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-border/60 align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
